const API_BASE_URL = 'https://cb-2.transfur.cn/api';

// 获取认证头
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// 通用请求函数
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// 公共API请求函数（不需要认证）
const publicApiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Public API request failed:', error);
    throw error;
  }
};

// 日历API
export const calendarAPI = {
  // 获取所有记录
  getEntries: (month?: string) => {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      // 未登录用户使用公共API
      const endpoint = `/public/calendar/entries${month ? `?month=${month}` : ''}`;
      return publicApiRequest(endpoint);
    }
    
    // 已登录用户使用认证API
    const endpoint = `/calendar/entries${month ? `?month=${month}` : ''}`;
    return apiRequest(endpoint);
  },

  // 创建记录
  createEntry: (entryData: any) => 
    apiRequest('/calendar/entries', {
      method: 'POST',
      body: JSON.stringify(entryData),
    }),

  // 更新记录
  updateEntry: (id: number, entryData: any) => 
    apiRequest(`/calendar/entries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(entryData),
    }),

  // 删除记录
  deleteEntry: (id: number) => 
    apiRequest(`/calendar/entries/${id}`, {
      method: 'DELETE',
    }),

  // 获取统计数据
  getStatistics: (month?: string) => {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      // 未登录用户使用公共API
      const endpoint = `/public/calendar/statistics${month ? `?month=${month}` : ''}`;
      return publicApiRequest(endpoint);
    }
    
    // 已登录用户使用认证API
    const endpoint = `/calendar/statistics${month ? `?month=${month}` : ''}`;
    return apiRequest(endpoint);
  },

  // 批量操作
  batchUpdate: (entries: any[]) => 
    apiRequest('/calendar/batch', {
      method: 'POST',
      body: JSON.stringify({ entries }),
    }),

  // 上传图片
  uploadImage: (file: File, date: string) => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('image', file);
    formData.append('date', date);

    const url = `${API_BASE_URL}/upload-image`;
    const config = {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData,
    };

    return fetch(url, config).then(response => {
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      return response.json();
    });
  },
};

// 认证API
export const authAPI = {
  // 登录
  login: (credentials: { username: string; password: string }) => 
    apiRequest('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  // 注册
  register: (userData: { username: string; password: string; role?: string }) => 
    apiRequest('/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  // 登出
  logout: () => 
    apiRequest('/logout', {
      method: 'POST',
    }),
};

// 用户API
export const userAPI = {
  // 获取当前用户信息
  getCurrentUser: () => 
    apiRequest('/user/me'),

  // 获取用户列表（管理员）
  getUsers: () => 
    apiRequest('/users'),

  // 创建用户（管理员）
  createUser: (userData: any) => 
    apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  // 更新用户（管理员）
  updateUser: (id: number, userData: any) => 
    apiRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    }),

  // 删除用户（管理员）
  deleteUser: (id: number) => 
    apiRequest(`/users/${id}`, {
      method: 'DELETE',
    }),
};